import { useId } from 'react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { AXIS_PROPS, CHART } from './tokens'
import { ChartTooltip } from './ChartTooltip'

export type TimeSeriesChartType = 'area' | 'bar' | 'line'

export type ChartSeries = {
  key: string
  label: string
  color: string
}

export type TimeSeriesChartProps = {
  data: ReadonlyArray<Record<string, string | number>>
  xKey: string
  series: ReadonlyArray<ChartSeries>
  chartType: TimeSeriesChartType
  stacked?: boolean
  stackOffset?: 'expand'
  valueFormatter: (value: number) => string
  yTickFormatter?: (value: number) => string
  yAxisWidth?: number
  animate?: boolean
}

/**
 * Compact, unit-free Y-axis tick labels. The series unit (m³, %, …) is shown
 * once in the tooltip and the card's total header, never repeated per tick —
 * repeating it forced the label to wrap and widened the tick area until the
 * leading digit (and negative sign) was clipped.
 */
const compactTickFormatter = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M`
  if (abs >= 1_000) return `${(value / 1_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k`
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
}

const CHART_BY_TYPE = {
  area: AreaChart,
  bar: BarChart,
  line: LineChart,
} as const

const TOP_RADIUS: [number, number, number, number] = [3, 3, 0, 0]

const activeDotFor = (color: string) => ({ r: 3, fill: color, strokeWidth: 0 })

export const TimeSeriesChart = ({
  data,
  xKey,
  series,
  chartType,
  stacked = false,
  stackOffset,
  valueFormatter,
  yTickFormatter = compactTickFormatter,
  yAxisWidth = 48,
  animate = true,
}: TimeSeriesChartProps) => {
  const uid = useId()
  const Chart = CHART_BY_TYPE[chartType]
  const stackId = stacked ? 'stack' : undefined

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Chart data={data as Array<Record<string, string | number>>} margin={CHART.margin} stackOffset={stackOffset}>
        {chartType === 'area' && (
          <defs>
            {series.map((s, i) => (
              <linearGradient key={s.key} id={`areaGrad-${uid}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={CHART.areaFillOpacity * 2} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
        )}
        <CartesianGrid strokeDasharray={CHART.gridDash} stroke={CHART.gridStroke} vertical={false} />
        <XAxis dataKey={xKey} {...AXIS_PROPS} interval="equidistantPreserveStart" minTickGap={40} />
        <YAxis {...AXIS_PROPS} tickFormatter={yTickFormatter} width={yAxisWidth} />
        <Tooltip
          content={<ChartTooltip valueFormatter={valueFormatter} />}
          cursor={{ stroke: CHART.cursorStroke, strokeDasharray: CHART.cursorDash }}
        />
        {series.map((s, i) => {
          const common = {
            key: s.key,
            dataKey: s.key,
            name: s.label,
            isAnimationActive: animate,
            animationDuration: CHART.animationDuration,
            animationEasing: CHART.animationEasing,
          }
          if (chartType === 'bar') {
            return (
              <Bar
                {...common}
                stackId={stackId}
                fill={s.color}
                fillOpacity={CHART.barFillOpacity}
                radius={i === series.length - 1 ? TOP_RADIUS : undefined}
              />
            )
          }
          if (chartType === 'line') {
            return (
              <Line
                {...common}
                stroke={s.color}
                strokeWidth={CHART.strokeWidth}
                dot={false}
                activeDot={activeDotFor(s.color)}
              />
            )
          }
          return (
            <Area
              {...common}
              stackId={stackId}
              stroke={s.color}
              strokeWidth={CHART.strokeWidth}
              fill={`url(#areaGrad-${uid}-${i})`}
              dot={false}
              activeDot={activeDotFor(s.color)}
            />
          )
        })}
      </Chart>
    </ResponsiveContainer>
  )
}
